/**
 *
 * @author Matthew Rawson @ 19/10/2017 13:07
 * @notes
 *
 * ### Usage
 *
 * `<Header id={id("header", this.props)} user={user} userTenant={userTenant} apps={apps} />`
 *
 * or
 *
 * `<Header id={id("header", "example-existing-id")} user={user} userTenant={userTenant} apps={apps} />`
 *
 * or
 *
 * `<Header id={id("header", this.props.id)} user={user} userTenant={userTenant} apps={apps} />`
 *
 * or
 *
 * `<Header
 *      id={id("header", this.props, {useUrl: true, prefix: "im-a-prefix"})}
 *      user={user}
 *      userTenant={userTenant}
 *      apps={apps} />`
 *
 *
 */
import {get, isString, isNumber} from "lodash"

const DIVIDER = "__"

/**
 * Ensure only valid characters are used as part of the id namespace
 *
 * @param namespace
 * @returns {*}
 * @private
 */
const _replaceInvalidCharacters = (namespace) => {
  const re = /[^A-Za-z0-9-_.:]/g
  return isString(namespace) ? namespace.replace(re, "-") : namespace
}

/**
 * Ensure only valid characters are used as part of the id namespace
 *
 * @param namespace
 * @returns {*}
 * @private
 */
const _ensureFirstCharacterIsValid = (namespace) => {
  /** Just return itself if it isn't a string */
  if (!isString(namespace)) return namespace

  /** RE to find if the first charcter isn't a letter */
  const re = /^[^A-Za-z]/

  return namespace.match(re) ? `ID__${namespace}` : namespace
}

/**
 * @notes Helper to make sure to include only valid prefixes, then separate them with the divider
 * @param namespaces
 * @returns {string}
 * @private
 */
const _namespaceJoiner = (namespaces) => {
  let namespace = namespaces.filter((ns) => isNumber(ns) || (isString(ns) && !!ns)).join(DIVIDER)

  /** If namespace is undefined just return it */
  if (!namespace) return namespace

  /** Other wise run string formatting operations */
  namespace = _replaceInvalidCharacters(namespace)
  namespace = _ensureFirstCharacterIsValid(namespace)

  return namespace
}

/**
 * @notes Returns the window location pathname replacing the forward slashes with the namespace divider symbol
 * @returns {string}
 * @private
 */
const _getURLPrefix = () => {
  /** get base pathname */
  let pathname = window.location.pathname

  /** trim starting and trailing slashes */
  pathname = pathname.replace(/^\//, "").replace(/\/$/, "")

  /** replace all remaining slashes with the divider */
  return pathname.replace(/\//g, DIVIDER)
}

type Props = string | {id?: string}

interface Settings {
  prefix?: string
  useUrl?: boolean
  force?: boolean
}

/**
 * id() helper will return a formatted id
 *
 * @param suffix {string}
 * @param props {object|string}
 * @param settings {object}
 * @returns {string|undefined}
 */
const id = (suffix: string, props: Props, settings: Settings = {}) => {
  const existingIdNamespace = isString(props) ? props : get(props, "id")

  /** Expand settings */
  const {prefix, useUrl, force} = settings
  const urlPrefix = useUrl ? _getURLPrefix() : null

  /** By default return undefined if an idNamepsace is not present or it isn't forced to set */
  if (!force && !existingIdNamespace) return

  /** Return the formatted id */
  return _namespaceJoiner([existingIdNamespace, urlPrefix, prefix, suffix])
}

export default id
